import { Injectable, inject } from '@angular/core';
import { LoadingController } from '@ionic/angular';
import { BehaviorSubject, Observable } from 'rxjs';

export interface LoadingOptions {
  message?: string;
  duration?: number;
  spinner?: 'bubbles' | 'circles' | 'circular' | 'crescent' | 'dots' | 'lines' | 'lines-small';
  backdropDismiss?: boolean;
  cssClass?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LoaderService {
  private loadingController = inject(LoadingController);
  private loadingStates = new Map<string, HTMLIonLoadingElement>();
  private globalLoadingState = new BehaviorSubject<boolean>(false);

  // Observable for components to subscribe to global loading state
  get isLoading$(): Observable<boolean> {
    return this.globalLoadingState.asObservable();
  }

  get isLoading(): boolean {
    return this.globalLoadingState.value;
  }

  /**
   * Show a global loading indicator
   */
  async show(options: LoadingOptions = {}): Promise<HTMLIonLoadingElement> {
    const defaultOptions: LoadingOptions = {
      message: 'Loading...',
      spinner: 'crescent',
      backdropDismiss: false,
      ...options
    };

    const loading = await this.loadingController.create(defaultOptions);
    await loading.present();

    this.globalLoadingState.next(true);
    this.loadingStates.set('global', loading);

    return loading;
  }

  /**
   * Hide the global loading indicator
   */
  async hide(): Promise<void> {
    const loading = this.loadingStates.get('global');
    if (loading) {
      await loading.dismiss();
      this.loadingStates.delete('global');
    }
    this.globalLoadingState.next(false);
  }

  /**
   * Show a named loading indicator (allows multiple concurrent loaders)
   */
  async showWithId(id: string, options: LoadingOptions = {}): Promise<HTMLIonLoadingElement> {
    // If already showing with this ID, return existing loader
    if (this.loadingStates.has(id)) {
      return this.loadingStates.get(id)!;
    }

    const defaultOptions: LoadingOptions = {
      message: 'Loading...',
      spinner: 'crescent',
      backdropDismiss: false,
      ...options
    };

    const loading = await this.loadingController.create(defaultOptions);
    await loading.present();

    this.loadingStates.set(id, loading);
    return loading;
  }

  /**
   * Hide a named loading indicator
   */
  async hideWithId(id: string): Promise<void> {
    const loading = this.loadingStates.get(id);
    if (loading) {
      await loading.dismiss();
      this.loadingStates.delete(id);
    }
  }

  /**
   * Show loading with auto-hide after a delay
   */
  async showAutoHide(options: LoadingOptions & { delay?: number } = {}): Promise<void> {
    const { delay = 2000, ...loadingOptions } = options;
    await this.show(loadingOptions);

    setTimeout(() => {
      this.hide();
    }, delay);
  }

  /**
   * Show loading during an async operation
   */
  async showDuringAsync<T>(
    asyncOperation: () => Promise<T>,
    options: LoadingOptions = {}
  ): Promise<T> {
    await this.show(options);
    try {
      const result = await asyncOperation();
      return result;
    } finally {
      await this.hide();
    }
  }

  /**
   * Dismiss all active loading indicators
   */
  async dismissAll(): Promise<void> {
    const dismissPromises = Array.from(this.loadingStates.values()).map(loading =>
      loading.dismiss()
    );

    await Promise.all(dismissPromises);
    this.loadingStates.clear();
    this.globalLoadingState.next(false);
  }

  /**
   * Check if a specific loading indicator is active
   */
  isLoadingWithId(id: string): boolean {
    return this.loadingStates.has(id);
  }

  /**
   * Get the number of active loading indicators
   */
  get activeLoadersCount(): number {
    return this.loadingStates.size;
  }
}